import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { fromEvent, merge, from, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap, filter, switchMap, pluck, catchError } from 'rxjs/operators';
import { isBackspace } from 'spark-util/keyboardEvents';
import stateReducer from './multiselectReducer';
import { renderSelectItems } from './SelectItem';
import { SearchIcon } from './SelectInput';
import SelectedItemList from './SelectedItemList';
import SelectBase from './SelectBase';
import { ClearIcon } from './SelectInput';

const atLeastMinSearchLength = (minLength, text) => text && text.length >= minLength;

const AsyncWrapper = (props) => {
    const {
        label,
        placeholder,
        options: optionsProp,
        onChange,
        minSearchLength = 2,
        multiple,
        initialValue,
        disabled
    } = props;

    const searchRef = useRef(null);
    const optionsLoaderRef = useRef(optionsProp);
    optionsLoaderRef.current = optionsProp;
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);

    const reducer = useMemo(() => multiple ? stateReducer(true) : undefined, [multiple]);
    const disabledValue = props.disabledValue;
    const renderOption = useMemo(() => renderSelectItems(
        multiple
            ? () => false
            : (item, selected) => selected && item.value === selected.value
    ), [multiple]);

    const searchStarted = useCallback(() => setSearching(true), []);
    const searchFinished = useCallback(() => setSearching(false), []);
    const setAvailableOptions = useCallback((opts) => setOptions(opts), []);
    const clearOptions = useCallback(() => setOptions([]), []);
    const clearInputValue = useCallback(() => setOptions([]), []);

    useEffect(() => {
        if (!searchRef.current) return;

        const searchText = merge(
            fromEvent(searchRef.current, 'keyup'),
            fromEvent(searchRef.current, 'keydown').pipe(filter(isBackspace))
        )
            .pipe(
                pluck('target', 'value'),
                distinctUntilChanged(),
                tap(clearOptions),
                tap(searchStarted),
                debounceTime(150),
                filter(search => atLeastMinSearchLength(minSearchLength, search)),
                switchMap(searchText =>
                    from(optionsLoaderRef.current(searchText)).pipe(catchError(() => of([])))
                ),
                tap(searchFinished)
            )
            .subscribe(setAvailableOptions);

        return () => {
            searchText.unsubscribe();
            setSearching(false);
        };
    }, [minSearchLength, clearOptions, searchStarted, searchFinished, setAvailableOptions]);

    const filterOptions = useCallback(({ selectedItem }) => item => {
        return !selectedItem.some(selected => selected.value === item.value);
    }, []);

    const onBlurHandler = useCallback(event => {
        props.onBlur && props.onBlur(event);
    }, [props.onBlur]);

    const helpText = useCallback(({ inputValue }) =>
        atLeastMinSearchLength(minSearchLength, inputValue)
            ? searching
                ? 'Searching...'
                : 'No results found'
            : 'Start typing to search...',
        [minSearchLength, searching]
    );

    const renderSearchIcon = useCallback((disabled) => () => <SearchIcon disabled={disabled} />, []);
    const renderInputDecoration = useCallback(disabled => ({ selectedItem, clearSelection }) =>
        !selectedItem || disabled ? (
            <SearchIcon />
        ) : (
            <span onClick={clearSelection}>
                <ClearIcon />
            </span>
        ),
        []
    );

    const blankStr = () => '';
    const selectedValueText = item => (item ? item.label : '');

    const value = initialValue
        ? initialValue
        : multiple
            ? []
            : '';

    return (
        <SelectBase
            label={label}
            placeholder={placeholder}
            options={options}
            initialValue={value}
            stateReducer={reducer}
            noResultsText={helpText}
            inputRef={searchRef}
            onChange={onChange}
            onSelect={clearOptions}
            onBlur={onBlurHandler}
            onInputClick={clearInputValue}
            disabledValueReport={disabledValue}
            disabled={disabled}
            selectedItemToString={multiple ? blankStr : selectedValueText}
            filterOptions={multiple && filterOptions}
            renderOption={renderOption}
            renderSelected={multiple && SelectedItemList}
            renderInputDecoration={
                multiple
                    ? renderSearchIcon(disabled)
                    : renderInputDecoration(disabled)
            }
        />
    );
};

export default AsyncWrapper;
